import { formatGoalPeriodAnchorLabel } from "../../../utils/goalMetricPeriodUtils"
import {
  formatCellByType,
  formatCompactNumber,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatPlatformLabel,
  formatExportGoalValue,
  formatGoalPeriodLabel,
  formatGoalProgressStatus,
  formatReportDate,
  formatReportHour,
  formatTableRow,
  sliceSeriesForReport,
  sumField,
} from "./dashboardReportFormatters"
import {
  formatTopPostDisplayDate,
  formatTopPostEngagementRate,
  getTopPostDateValue,
  platformFromPostLink,
  topPostDashboardPlatformKey,
} from "../../../lib/utils/topPostsReport"
import { dashboardPlatformKeyFromDescription } from "../../../lib/utils/socialNetworkCatalog"
import { colorVarForAlertTypeLabel } from "../../../lib/utils/alertsReport"
import { campaignChartRowsHaveMetrics } from "../../../lib/utils/campaignPerformanceReport"
import {
  buildPaidCampaignSummaries,
  buildPaidObjectiveSummaries,
  mapPaidPerformanceCampaigns,
  paidCampaignRowsToSummaries,
  paidObjectiveRowsToSummaries,
} from "../../../lib/utils/paidPerformanceReport"
import { compareValues, formatTableCell, sortByMetric, TABLE_COLUMNS } from "../topPerformingAds/topPerformingAdsData"
import { normalizeDashboardPlatformKey } from "../../../lib/utils/socialPerformanceReport"
import { parseGoalMetricNumber } from "../../../lib/utils/ytdPagePerformanceReport"
import { buildMessageHeatmapInsight, formatHourShort } from "./messageHeatmapReadability"
import { parseHourLabel12ToIndex } from "./optimalPostingTimesAdapter"
import { buildOptimalPostingInsight, weeklyWeightedEngagementMatrix24 } from "./optimalPostingTimesReadability"
import { OPTIMAL_POSTING_WIDGET_SUBTITLE, getHeatmapQuartiles } from "./optimalPostingTimesUtils"
import { deriveWeekdayPostingOpportunities } from "./optimalPostingOpportunities"
import {
  formatDemographicsFollowerShare,
  geographicFollowerCount,
} from "../../../lib/utils/demographicsFollowers"
import { buildSocialPerformanceBenchmarkRichHtml } from "./socialPerformanceBenchmarkReport"

function exportRowPlatform(row) {
  if (!row) return ""
  const key = topPostDashboardPlatformKey(row)
  if (key && key !== "all") return key
  const fromNetwork = dashboardPlatformKeyFromDescription(row.socialNetwork)
  if (fromNetwork && fromNetwork !== "all") return fromNetwork
  const fromLink = platformFromPostLink(
    row.postLink || row.permalink || row.post_media_link || row.postLinkUrl,
  )
  if (fromLink) return fromLink
  const raw = String(row.socialNetwork || row.platformKey || row.platform || "").trim()
  if (raw && raw.toLowerCase() !== "all" && raw.toLowerCase() !== "all platforms") return raw
  return ""
}

function buildTable(title, subtitle, columns, rows, columnTypes, options = {}) {
  if (!columns?.length || !rows?.length) return null
  return {
    title,
    subtitle,
    columns,
    columnTypes,
    rawRows: rows,
    rows: rows.map((row) => formatTableRow(row, columnTypes)),
    className: options.className || "",
    columnWeights: options.columnWeights || null,
    insight: options.insight || "",
    bundleTitle: options.bundleTitle || "",
    bundleSubtitle: options.bundleSubtitle || "",
  }
}

export function platformComparisonDisplayRows(ctx = {}) {
  const rows = Array.isArray(ctx.platformComparisonRows) ? ctx.platformComparisonRows : []
  const connectedKeys = Array.isArray(ctx.monthlySocialConnectedPlatformKeys)
    ? ctx.monthlySocialConnectedPlatformKeys.map(normalizeDashboardPlatformKey)
    : null

  if (connectedKeys?.length) {
    const byPlatform = new Map(
      rows.map((row) => [normalizeDashboardPlatformKey(row.platform), row]),
    )
    return connectedKeys.map((key) => {
      const existing = byPlatform.get(key)
      if (existing) return existing
      return { platform: key, engagementRate: 0, lifetimeEngagements: 0 }
    })
  }

  return rows.filter(
    (row) => Number(row.engagementRate) > 0 || Number(row.lifetimeEngagements) > 0,
  )
}

const SIDE_BY_SIDE_CHART_TABLE_OPTIONS = {
  className: "report-table-wrap--chart-data",
  columnWeights: [1.42, 1.08, 1.08],
}

const AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS = {
  className: "report-table-wrap--chart-data report-table-wrap--audience-inline",
}

function buildPlatformComparisonDataTable(rows) {
  if (!Array.isArray(rows) || !rows.length) return null
  return buildTable(
    "",
    "",
    ["Platform", "Engagement rate", "Lifetime engagement"],
    rows.map((r) => [
      r.platform,
      r.engagementRate,
      r.lifetimeEngagements ?? 0,
    ]),
    ["platformIcon", "percentRaw", "number"],
    {
      ...SIDE_BY_SIDE_CHART_TABLE_OPTIONS,
      columnWeights: [0.5, 1.25, 1.25],
    },
  )
}

function emptyContent(description, note) {
  return {
    description,
    summaryMetrics: [],
    tables: [],
    notes: note ? [note] : [],
    empty: true,
  }
}

function formatGoalCurrentForExport(goal) {
  const current = parseGoalMetricNumber(goal?.current)
  if (current == null) return "—"
  return formatExportGoalValue(current, goal?.format)
}

function formatGoalTargetForExport(goal) {
  const target = Number(goal?.target)
  if (!Number.isFinite(target) || target <= 0) return "No target"
  return formatExportGoalValue(goal.target, goal?.format)
}

function formatGoalPeriodForExport(goal) {
  const period = formatGoalPeriodLabel(goal?.period)
  const ends = String(goal?.periodEndLabel || "").trim()
  if (ends && ends !== "—") return `${period} · ${ends}`
  return period
}

function formatGoalPerformanceDuration(goal) {
  const labeled = String(goal?.durationLabel || "").trim()
  if (labeled) return labeled
  return formatGoalPeriodAnchorLabel(goal?.period, goal?.periodStart)
}

function formatGoalPerformanceStatus(status) {
  if (status === "exceeded" || status === "achieved") return "Achieved"
  if (status === "on-track") return "On track"
  if (status === "needs-attention" || status === "behind") return "Needs Attention"
  return "At Risk"
}

function goalExportStatusKey(status) {
  if (status === "exceeded" || status === "achieved" || status === "on-track") return "on-track"
  if (status === "needs-attention" || status === "behind") return "needs-attention"
  return "at-risk"
}

function formatGoalPerformanceProgress(goal) {
  const labeled = String(goal?.goalProgressLabel || "").trim()
  if (labeled) return labeled
  const ratio = Number(goal?.progressRatio)
  if (!Number.isFinite(ratio)) return "—"
  return `${Math.round(ratio * 100)}%`
}

function buildGoalPerformanceExportContent(ctx) {
  const goals = ctx.activeGoals || []
  if (!goals.length) {
    return emptyContent("Goal performance.", "No active goals configured.")
  }
  const rows = goals.map((goal) => {
    const ratio = Number(goal?.progressRatio)
    const progress = Number.isFinite(ratio) ? Math.round(ratio * 100) : 0
    return {
      name: goal?.name || "",
      platform: goal?.platform || "",
      duration: formatGoalPerformanceDuration(goal),
      progress,
      progressLabel: formatGoalPerformanceProgress(goal),
      actual: formatGoalCurrentForExport(goal),
      target: formatGoalTargetForExport(goal),
      statusKey: goalExportStatusKey(goal?.status),
      statusLabel: formatGoalPerformanceStatus(goal?.status),
    }
  })
  const onTrackCount = rows.filter((row) => row.statusKey === "on-track").length
  const needsAttentionCount = rows.filter((row) => row.statusKey === "needs-attention").length
  const atRiskCount = Math.max(0, rows.length - onTrackCount - needsAttentionCount)
  return {
    description: "Track progress against your active goals.",
    summaryMetrics: [
      { label: "Metrics", value: formatNumber(rows.length) },
      { label: "On track", value: formatNumber(onTrackCount) },
      { label: "Needs attention", value: formatNumber(needsAttentionCount) },
      ...(atRiskCount > 0 ? [{ label: "At risk", value: formatNumber(atRiskCount) }] : []),
    ],
    tables: [
      buildTable(
        "",
        "",
        ["Metric", "Duration", "Progress", "Actual / Target", "Status"],
        rows.map((row) => [
          row.platform ? `${row.name} (${row.platform})` : row.name,
          row.duration,
          row.progressLabel,
          `${row.actual} / ${row.target}`,
          row.statusLabel,
        ]),
        ["text", "text", "text", "text", "text"],
        {
          className: "report-table-wrap--goals",
          columnWeights: [2.2, 0.9, 0.8, 1.4, 1.1],
        },
      ),
    ],
    goalChart: {
      metricsCount: rows.length,
      onTrackCount,
      needsAttentionCount,
      atRiskCount,
      rows,
    },
    notes: [],
  }
}

function shareListHasData(items) {
  return (items || []).some((item) => Number(item.value ?? item.count ?? item.share) > 0)
}

function audiencePlatformFollowers(row) {
  const facebook = Number(row?.facebook) || 0
  const instagram = Number(row?.instagram) || 0
  if (facebook > 0 || instagram > 0) {
    return { facebook, instagram, total: facebook + instagram }
  }
  const total =
    Number(row?.total) ||
    Number(row?.value) ||
    Number(row?.followers) ||
    Number(row?.count) ||
    0
  return { facebook: 0, instagram: 0, total }
}

function audienceAgeRowHasData(row) {
  if (row == null) return false
  if (row.female != null || row.male != null || row.other != null) {
    return (Number(row.female) || 0) + (Number(row.male) || 0) + (Number(row.other) || 0) > 0
  }
  return Number(row.value ?? row.share) > 0
}

function audiencePlaceRowHasData(row) {
  return audiencePlatformFollowers(row).total > 0
}

export function audienceDemographicsHaveData(ctx = {}) {
  const ageRows = ctx.exportAudienceAgeGender ?? ctx.audienceAgeGender ?? []
  const cities = ctx.exportAudienceTopCities ?? ctx.audienceTopCities ?? []
  const countries = ctx.exportAudienceTopCountries ?? ctx.audienceTopCountries ?? []
  const geographic = ctx.audienceGeographic ?? []
  return (
    ageRows.some(audienceAgeRowHasData) ||
    cities.some(audiencePlaceRowHasData) ||
    countries.some(audiencePlaceRowHasData) ||
    geographic.some((row) => Number(row?.count ?? row?.value ?? row?.followers) > 0)
  )
}

export function sentimentMonitoringHasData(ctx = {}) {
  const total = Number(ctx.sentimentTotalMessages) || 0
  if (total > 0) return true
  if (shareListHasData(ctx.sentimentShare)) return true
  const series = ctx.sentimentSeries || []
  return series.some(
    (row) =>
      (Number(row.positive) || 0) +
        (Number(row.neutral) || 0) +
        (Number(row.negative) || 0) +
        (Number(row.messages) || 0) >
      0,
  )
}

function heatmapMatrixHasData(matrix) {
  if (!Array.isArray(matrix) || !matrix.length) return false
  return matrix.some((row) => Array.isArray(row) && row.some((v) => Number(v) > 0))
}

function messageHeatmapHasExportData(ctx = {}) {
  if (heatmapMatrixHasData(ctx.messageHeatmapValues)) return true
  if (heatmapMatrixHasData(ctx.messageHeatmapWeeklyValues)) return true
  const matrix24 = ctx.messageHeatmapMatrix24
  if (matrix24 && heatmapMatrixHasData(matrix24)) return true
  const daily = ctx.messageHeatmapHourlyDailyMatrix?.matrix
  if (daily && heatmapMatrixHasData(daily)) return true
  const monthly = ctx.messageHeatmapMonthlyHourlyBundle?.matrix
  if (monthly && heatmapMatrixHasData(monthly)) return true
  return false
}

function platformComparisonHasExportData(ctx = {}) {
  const rows = platformComparisonDisplayRows(ctx)
  if (rows.length) {
    if (Array.isArray(ctx.monthlySocialConnectedPlatformKeys) && ctx.monthlySocialConnectedPlatformKeys.length > 0) {
      return true
    }
    return rows.some(
      (r) => Number(r.engagementRate) > 0 || Number(r.lifetimeEngagements) > 0,
    )
  }
  const seriesMap = ctx.platformSeries || {}
  const platforms = Object.keys(seriesMap).filter((p) => seriesMap[p]?.length)
  if (!platforms.length) return false
  return platforms.some((platform) =>
    sliceSeriesForReport(seriesMap[platform], 30).some((row) => Number(row.engagements) > 0),
  )
}

const POST_HEATMAP_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

function rankedSlotsFromPostHeatmap(heat) {
  if (!heat) return []
  if (Array.isArray(heat.values) && heat.values.length) {
    return [...heat.values]
      .map((v) => ({
        day: v.day ?? v.dayLabel,
        hour: v.hour ?? v.hourLabel,
        count: Number(v.value ?? v.count) || 0,
      }))
      .filter((v) => v.count > 0)
      .sort((a, b) => b.count - a.count)
  }
  const samples = heat.weeklyData?.samples
  if (Array.isArray(samples) && samples.length) {
    const slots = []
    samples.forEach((dayRow, dayIdx) => {
      if (!Array.isArray(dayRow)) return
      dayRow.forEach((count, hour) => {
        const n = Number(count) || 0
        if (n > 0) {
          slots.push({
            day: POST_HEATMAP_DAY_LABELS[dayIdx] ?? String(dayIdx),
            hour,
            count: n,
          })
        }
      })
    })
    return slots.sort((a, b) => b.count - a.count)
  }
  return []
}

function postHeatmapHasExportData(heat) {
  if (!heat) return false
  if (rankedSlotsFromPostHeatmap(heat).length > 0) return true
  if (heatmapMatrixHasData(heat.matrix24)) return true
  if (heatmapMatrixHasData(heat.valuesMatrix)) return true
  if (Array.isArray(heat.rows) && heat.rows.length) {
    return heat.rows.some(
      (r) =>
        Number(r.postCount ?? r.messageCount ?? r.count ?? r.post_count) > 0,
    )
  }
  return false
}

const AUDIENCE_EXPORT_MAX_PLACE_ROWS = 20

function tableHasMeaningfulRows(table) {
  if (!table) return false
  const rows = table.rawRows ?? table.rows
  if (!Array.isArray(rows) || !rows.length) return false
  const columnTypes = table.columnTypes || []
  const numericColIndexes = columnTypes
    .map((type, index) =>
      type === "number" || type === "percent" || type === "compact" ? index : -1,
    )
    .filter((index) => index >= 0)

  if (numericColIndexes.length > 0) {
    const hasPositiveNumeric = rows.some((row) => {
      if (!Array.isArray(row)) return false
      return numericColIndexes.some((colIndex) => Number(row[colIndex]) > 0)
    })
    if (hasPositiveNumeric) return true
  }

  return rows.some((row) => {
    if (!Array.isArray(row)) return false
    return row.some((cell, colIndex) => {
      const type = columnTypes[colIndex]
      if (type === "competitor") {
        const name = cell && typeof cell === "object" ? cell.name : cell
        return String(name ?? "").trim() !== ""
      }
      if (type === "metricDelta") {
        const v = cell && typeof cell === "object" ? cell.value : cell
        return String(v ?? "").trim() !== "" && String(v).trim() !== "—"
      }
      if (type === "text" || type === "date" || type === "platform" || type === "link" || type === "goalStatus" || type === "growth") {
        return String(cell ?? "").trim() !== ""
      }
      const n = Number(cell)
      return Number.isFinite(n) && n > 0
    })
  })
}

export function reportContentHasDisplayableData(content) {
  if (!content || content.empty) return false
  if (content.goalChart?.rows?.length) return true
  if (content.richHtml && String(content.richHtml).trim()) return true
  const tables = content.tables || []
  if (tables.some((t) => tableHasMeaningfulRows(t))) return true
  const metrics = content.summaryMetrics || []
  return metrics.some((m) => {
    const v = m?.value
    if (v == null || v === "" || v === "—") return false
    const n = Number(String(v).replace(/[^0-9.-]/g, ""))
    if (Number.isFinite(n)) return n > 0
    return String(v).trim().length > 0
  })
}

function withChartInsight(chartId, content, ctx) {
  if (!content || content.empty) return content
  if (
    chartId === "socialPerformanceBenchmark" &&
    String(content.richHtml || "").trim()
  ) {
    return content
  }
  if (String(content.insight || "").trim()) return content
  if ((content.tables || []).some((table) => String(table?.insight || "").trim())) {
    return content
  }
  const insight = buildChartInsightText(chartId, ctx)
  if (!insight) return content
  return { ...content, insight }
}

/** Match dashboard ChartInsightCallout copy when available. */
export function buildChartInsightText(chartId, ctx = {}) {
  switch (chartId) {
    case "messages": {
      const total = Number(ctx.totalMonitoringShare) || 0
      if (total <= 0) {
        return "No messages recorded for this date range. Adjust filters or check back when new activity arrives."
      }
      const lead = String(ctx.topMessagePlatform || "").trim()
      if (lead) {
        return `Messaging volume is led by ${lead} with a clear majority share. Focus on peak windows to keep response times low and reduce negative spikes.`
      }
      const platformCount = (ctx.monitoringShare || []).filter((i) => Number(i.value) > 0).length
      return `${formatCompactNumber(total)} messages received across ${platformCount || 1} platform${platformCount === 1 ? "" : "s"}`
    }
    case "messageSentiment": {
      const total = Number(ctx.sentimentTotalMessages) || 0
      if (total <= 0) {
        return "No sentiment data for this date range. Adjust filters or check back when new messages arrive."
      }
      const pos = Number(ctx.sentimentPositivePercent) || 0
      const neg = Number(ctx.sentimentNegativePercent) || 0
      const spikes = ctx.sentimentSpikeDates || "peak days"
      return `Brand sentiment is ${pos >= neg ? "strong" : "mixed"} with a ${pos >= neg ? "positive" : "neutral"} skew${neg > 0 ? " and some negative volume" : ""}. Focus on spikes around ${spikes} to reduce peak-day dips.`
    }
    case "alerts": {
      const total = Number(ctx.totalAlerts) || 0
      const prev = Number(ctx.previousTotalAlerts) || 0
      const spikes = ctx.alertSpikeDates || "peak days"
      if (total > 0) {
        return `Alert volume is concentrated in a few peak days. Focus on reducing negative-message spikes around ${spikes} and improving response workflows for time-sensitive categories.`
      }
      if (prev > 0) {
        return `No alerts in the current period (${formatNumber(prev)} in the previous period). Adjust filters or check back when new situations are reported.`
      }
      return "No alerts recorded for this date range. Adjust filters or check back when new situations are reported."
    }
    case "audienceInsights": {
      if (!audienceDemographicsHaveData(ctx)) return ""
      return ""
    }
    case "monthlySocialPerformance":
      return String(ctx.monthlySocialStrategicInsight || "").trim()
    case "socialPerformanceBenchmark": {
      const tabKey = ctx.socialPerformanceTab === "growth" ? "growth" : "engagement"
      const fromApi = ctx.socialPerformanceByTab?.[tabKey]?.strategicInsight
      return String(fromApi || ctx.competitorBenchmarkInsight || "").trim()
    }
    case "optimal_posting_times": {
      const heat = ctx.postHeatmap
      const matrix =
        heat?.matrix24 ||
        heat?.valuesMatrix ||
        (heat?.weeklyData?.matrix
          ? weeklyWeightedEngagementMatrix24(heat.weeklyData.matrix)
          : null)
      if (matrix) {
        return (
          buildOptimalPostingInsight(heat?.viewMode || "weekly", matrix, {
            weekDayLabels: heat?.weekDayLabels,
            calDates: heat?.calDates ?? heat?.calendarByDay?.dates,
            monthDates: heat?.monthDates ?? heat?.monthlyData?.dates,
          }) || ""
        )
      }
      return String(ctx.optimalPostingInsight || "").trim()
    }
    case "messageHeatmap": {
      if (ctx.messageHeatmapInsight) return String(ctx.messageHeatmapInsight).trim()
      const matrix = ctx.messageHeatmapMatrix24
      if (matrix) {
        return (
          buildMessageHeatmapInsight(ctx.messageHeatmapViewMode || "weekly", matrix, {
            wDays: ctx.messageHeatmapDays,
            calDates: ctx.messageHeatmapCalDates,
            monthDates: ctx.messageHeatmapMonthDates,
          }) || ""
        )
      }
      return ""
    }
    case "paidMediaPerformance": {
      const roas = Number(ctx.paidMediaTotals?.roas)
      if (!Number.isFinite(roas) || roas <= 0) return ""
      return `Paid campaigns are performing at ${roas.toFixed(1)}x ROAS in this period.`
    }
    case "socialGoalsOverview":
    case "goalPerformance":
      return String(ctx.goalsInsight || buildGoalsOverviewInsight(ctx)).trim()
    default:
      return String(ctx.chartInsights?.[chartId] || "").trim()
  }
}

function buildGoalsOverviewInsight(ctx = {}) {
  const summary = ctx.goalsStatusSummary
  const goals = ctx.activeGoals || []
  if (!goals.length) return ""
  if (!summary?.total) return ""
  const attention = (summary.behind || 0) + (summary["needs-attention"] || 0) + (summary["at-risk"] || 0)
  if (attention === 1) return "1 goal needs attention to stay on track"
  if (attention > 1) return `${attention} goals need attention to stay on track`
  const exceeded = summary.exceeded || 0
  if (exceeded > 0 && exceeded >= summary.total) {
    return "All active goals have met or exceeded their targets."
  }
  const onTrack = summary["on-track"] || 0
  if (onTrack > 0) {
    return `${onTrack} goal${onTrack === 1 ? "" : "s"} on track for this period.`
  }
  return ""
}

export function collectChartExportContent(chartId, ctx) {
  let content
  switch (chartId) {
    case "platformPerformanceComparison":
      content = buildPlatformComparisonContent(ctx)
      break
    case "campaignPerformance":
      content = buildCampaignPerformanceContent(ctx)
      break
    case "monthlySocialPerformance":
      content = buildMonthlySocialContent(ctx)
      break
    case "socialPerformanceBenchmark":
      content = buildCompetitiveBenchmarkContent(ctx)
      break
    case "topPosts":
      content = buildTopPostsContent(ctx)
      break
    case "topStories":
      content = buildTopStoriesContent(ctx)
      break
    case "optimal_posting_times":
      content = buildOptimalPostingContent(ctx)
      break
    case "paidMediaPerformance":
      content = buildPaidMediaPerformanceContent(ctx)
      break
    case "topPerformingAds":
    case "topPaidAds":
    case "topObjectives":
    case "topCampaigns":
    case "advertisingPerformance":
      content = buildPaidAdsWidgetContent(ctx, chartId)
      break
    case "audienceInsights":
      content = buildAudienceContent(ctx)
      break
    case "messages":
      content = buildMessagesContent(ctx)
      break
    case "messageSentiment":
      content = buildSentimentContent(ctx)
      break
    case "alerts":
      content = buildAlertsContent(ctx)
      break
    case "timeOfDay":
      content = buildTimeOfDayContent(ctx)
      break
    case "messageHeatmap":
      content = buildMessageHeatmapContent(ctx)
      break
    case "socialGoalsOverview":
      content = buildGoalsContent(ctx)
      break
    case "goalPerformance":
      content = buildGoalPerformanceExportContent(ctx)
      break
    default:
      content = emptyContent("Dashboard chart", "No export mapping for this chart.")
  }
  return withChartInsight(chartId, content, ctx)
}

/** Skip charts with no on-dashboard data (no empty pages in PDF/CSV). */
export function chartHasReportData(chartId, ctx = {}) {
  const content = collectChartExportContent(chartId, ctx)
  if (content.empty) return false

  if (chartId === "campaignPerformance") {
    const events = ctx.campaignEventRows || []
    if (!events.length || !campaignChartRowsHaveMetrics(events)) return false
  }
  if (chartId === "audienceInsights") {
    if (!audienceDemographicsHaveData(ctx)) return false
  }
  if (chartId === "messageSentiment") {
    if (!sentimentMonitoringHasData(ctx)) return false
  }
  if (chartId === "alerts") {
    if (!alertsMonitoringHasData(ctx)) return false
  }
  if (chartId === "timeOfDay") {
    const msg = (ctx.timeOfDayMessages || []).some((e) => Number(e.value) > 0)
    const alerts = (ctx.timeOfDayAlerts || []).some((e) => Number(e.value) > 0)
    if (!msg && !alerts) return false
  }
  if (chartId === "messageHeatmap") {
    if (!messageHeatmapHasExportData(ctx)) return false
  }
  if (chartId === "platformPerformanceComparison") {
    if (!platformComparisonHasExportData(ctx)) return false
  }
  if (chartId === "optimal_posting_times") {
    if (!postHeatmapHasExportData(ctx.postHeatmap)) return false
  }
  if (chartId === "messages") {
    const total = Number(ctx.totalMonitoringShare) || 0
    const series = ctx.monitoringSeries || []
    const seriesTotal = series.reduce(
      (s, row) => s + (Number(row.messages ?? row.value) || 0),
      0,
    )
    if (total === 0 && seriesTotal === 0) return false
  }
  if (chartId === "socialPerformanceBenchmark") {
    const byTab = ctx.socialPerformanceByTab
    const hasApiRows =
      (byTab?.engagement?.rows?.length ?? 0) > 0 || (byTab?.growth?.rows?.length ?? 0) > 0
    if (!hasApiRows) {
      const daily = ctx.competitorDaily || []
      const chartData = ctx.competitorChartData || []
      if (!daily.length && !chartData.length) return false
    }
  }
  if (!reportContentHasDisplayableData(content)) return false
  return true
}

function monthlySocialSourceLabel(source) {
  if (source === "organic") return "Organic"
  if (source === "paid") return "Paid"
  if (source === "paid_and_organic" || source === "all") return "Paid & organic"
  return "All sources"
}

function monthlySocialCompareLabel(compare) {
  if (compare === "yoy") return "Year over year (YoY)"
  if (compare === "mom") return "Month over month (MoM)"
  return null
}

/** @deprecated use collectChartExportContent */
export function collectChartExportBlocks(chartId, ctx) {
  return collectChartExportContent(chartId, ctx).tables
}

function buildPlatformComparisonContent(ctx) {
  const comparisonRows = platformComparisonDisplayRows(ctx)

  if (comparisonRows.length) {
    const comparisonWithData = comparisonRows.filter(
      (r) => Number(r.engagementRate) > 0 || Number(r.lifetimeEngagements) > 0,
    )
    const summaryRows = comparisonWithData.length ? comparisonWithData : comparisonRows
    return {
      description: "Compare engagement rate and lifetime engagement across connected platforms.",
      summaryMetrics: summaryRows.slice(0, 6).map((r) => ({
        label: `${formatPlatformLabel(r.platform)} engagement rate`,
        value: formatPercent(r.engagementRate, 1),
        hint: `${formatCompactNumber(r.lifetimeEngagements ?? 0)} lifetime engagements`,
      })),
      tables: [
        buildPlatformComparisonDataTable(comparisonRows),
      ].filter(Boolean),
      notes: [],
    }
  }

  const seriesMap = ctx.platformSeries || {}
  const platforms = Object.keys(seriesMap).filter((p) => seriesMap[p]?.length)
  if (!platforms.length) {
    return emptyContent(
      "Compare engagement and reach across connected platforms.",
      "No platform performance data in the current date range.",
    )
  }

  const windowRows = sliceSeriesForReport(seriesMap[platforms[0]], 14)
  const dates = windowRows.map((r) => r.date)

  const summaryMetrics = platforms.map((platform) => {
    const slice = sliceSeriesForReport(seriesMap[platform], 30)
    return {
      label: `${formatPlatformLabel(platform)} engagements`,
      value: formatCompactNumber(sumField(slice, "engagements")),
      hint: "Last 30 days",
    }
  })

  const columns = ["Date", ...platforms.map((p) => `${formatPlatformLabel(p)} engagements`)]
  const columnTypes = ["date", ...platforms.map(() => "number")]
  const rows = dates.map((date) => {
    const row = [date]
    platforms.forEach((platform) => {
      const point = seriesMap[platform].find((r) => r.date === date)
      row.push(point?.engagements ?? 0)
    })
    return row
  })

  return {
    description: "Platform-level engagement trends for the reporting window.",
    summaryMetrics,
    tables: [
      buildTable(
        "Daily engagement by platform",
        "Most recent days in range (up to 14)",
        columns,
        rows,
        columnTypes,
      ),
    ],
    notes: [],
  }
}

function buildCampaignPerformanceContent(ctx) {
  const events = ctx.campaignEventRows || []
  if (!events.length || !campaignChartRowsHaveMetrics(events)) {
    return emptyContent(
      "Event performance for hospitality campaigns.",
      "No event post metrics in the selected date range.",
    )
  }
  const series = ctx.campaignBarSeries || []
  const columns = ["Event", ...series.map((s) => s.label)]
  const columnTypes = ["text", ...series.map(() => "number")]
  const rows = events.map((e) => [
    e.eventName ?? e.name ?? "Event",
    ...series.map((s) => e[s.key] ?? 0),
  ])

  return {
    description: "Post performance across key hospitality events.",
    summaryMetrics: [
      { label: "Events", value: formatNumber(events.length) },
      {
        label: "Total impressions",
        value: formatCompactNumber(
          events.reduce((s, e) => s + (Number(e.impressions) || 0), 0),
        ),
      },
      {
        label: "Total engagements",
        value: formatCompactNumber(
          events.reduce((s, e) => s + (Number(e.engagements) || 0), 0),
        ),
      },
    ],
    tables: [
      buildTable(
        "Event post performance",
        "Matches the Event Performance chart",
        columns,
        rows,
        columnTypes,
      ),
    ],
    notes: [],
  }
}

function normalizeMonthlySocialColumnDefs(columns = []) {
  return columns.map((col) => {
    if (col && typeof col === "object") {
      return {
        key: col.key ?? col.apiMetricKey ?? String(col.label ?? ""),
        label: col.label ?? String(col.key ?? ""),
        isRate: Boolean(col.isRate),
      }
    }
    const key = String(col)
    return {
      key,
      label: key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase()),
      isRate: /rate/i.test(key),
    }
  })
}

function monthlySocialColumnType(colDef) {
  return "metricDelta"
}

function formatMonthlySocialDelta(value, comparisonType) {
  if (!comparisonType || comparisonType === "none") {
    return { label: "", tone: "empty" }
  }
  if (value == null || Number.isNaN(Number(value))) {
    return { label: "", tone: "empty" }
  }
  const rounded = Math.round(Number(value) * 10) / 10
  const suffix = comparisonType === "yoy" ? "YoY" : "MoM"
  const label = rounded === 0 ? `0% ${suffix}` : `${rounded > 0 ? "+" : ""}${rounded}% ${suffix}`
  return { label, tone: rounded < 0 ? "down" : "up" }
}

function monthlySocialExportCell(row, col, comparePeriod) {
  let value = "—"
  if (row?.values && typeof row.values === "object") {
    const val = row.values[col.key]
    if (val != null && String(val).trim() !== "") value = String(val)
  } else {
    const flat = row?.[col.key]
    if (flat != null && String(flat).trim() !== "") value = String(flat)
  }
  const deltaRaw =
    comparePeriod && comparePeriod !== "none"
      ? row?.comparisons?.[comparePeriod]?.[col.key]
      : null
  const delta = formatMonthlySocialDelta(deltaRaw, comparePeriod)
  return {
    kind: "metricDelta",
    value,
    delta: delta.label,
    deltaTone: delta.tone,
  }
}

function isMonthlySocialExportRowAllowed(row, connectedKeys) {
  if (row?.empty) return false
  if (!Array.isArray(connectedKeys) || connectedKeys.length === 0) return true
  const key = normalizeDashboardPlatformKey(row?.platform)
  if (!key || key === "all") return true
  return connectedKeys.some((connected) => normalizeDashboardPlatformKey(connected) === key)
}

function parseMonthlySocialMetricNumber(value) {
  if (value == null) return 0
  const s = String(value).trim()
  if (!s || s === "—" || s === "–" || s === "-") return 0
  const normalized = s.replace(/[%,$]/g, "").replace(/,/g, "").trim()
  if (!normalized) return 0
  const n = Number(normalized)
  return Number.isFinite(n) ? n : 0
}

function monthlySocialRowsHaveMetricData(rows, columnDefs, connectedKeys) {
  const visibleRows = (rows || []).filter((row) => isMonthlySocialExportRowAllowed(row, connectedKeys))
  if (!visibleRows.length || !columnDefs.length) return false
  return visibleRows.some((row) =>
    columnDefs.some((col) => {
      const raw = row?.values?.[col.key] ?? row?.[col.key]
      return parseMonthlySocialMetricNumber(raw) !== 0
    }),
  )
}

function buildMonthlySocialPlatformTable(title, subtitle, columnDefs, rows, comparePeriod, connectedKeys) {
  const visibleRows = (rows || []).filter((row) => isMonthlySocialExportRowAllowed(row, connectedKeys))
  if (!columnDefs.length || !visibleRows.length) return null
  const displayCols = ["Platform", ...columnDefs.map((c) => c.label)]
  const columnTypes = ["platform", ...columnDefs.map(() => monthlySocialColumnType())]
  const columnWeights = [
    2.35,
    ...columnDefs.map((col) => {
      const len = String(col.label || "").length
      if (len > 14) return 1.35
      if (len > 9) return 1.15
      return 1
    }),
  ]
  const tableRows = visibleRows.map((row) => [
    row.platform || row.label || "—",
    ...columnDefs.map((col) => monthlySocialExportCell(row, col, comparePeriod)),
  ])
  return buildTable(title, subtitle, displayCols, tableRows, columnTypes, {
    className: "report-table-wrap--dense report-table-wrap--monthlySocial",
    columnWeights,
  })
}

const MONTHLY_SOCIAL_DASHBOARD_SUBTITLE =
  "All data is sourced from native platforms and aligned with Meta Business Suite reporting standards."

function buildMonthlySocialContent(ctx) {
  const sourceKey =
    ctx.monthlySocialSource === "organic"
      ? "organic"
      : ctx.monthlySocialSource === "paid"
        ? "paid"
        : "all"
  const bySource = ctx.monthlySocialRowsBySource || {}
  const columnDefs = normalizeMonthlySocialColumnDefs(ctx.monthlySocialColumns || [])
  const exportAllSources = ctx.exportAllDataChartIds?.includes("monthlySocialPerformance")
  const connectedKeys = ctx.monthlySocialConnectedPlatformKeys
  const dataRows = (
    (exportAllSources && bySource.all?.length ? bySource.all : null) ??
    (bySource[sourceKey]?.length ? bySource[sourceKey] : null) ??
    ctx.monthlySocialRows ??
    []
  ).filter((row) => isMonthlySocialExportRowAllowed(row, connectedKeys))

  const sourceNote = exportAllSources
    ? "All / Organic / Paid"
    : monthlySocialSourceLabel(ctx.monthlySocialSource)
  const comparePeriod = ctx.monthlySocialComparePeriod || "mom"
  const compareNote = monthlySocialCompareLabel(comparePeriod)
  const viewMeta = [`View: ${sourceNote}`, compareNote].filter(Boolean).join(" · ")

  if (!columnDefs.length || !dataRows.length) {
    return {
      ...emptyContent(MONTHLY_SOCIAL_DASHBOARD_SUBTITLE, "No social performance data available."),
      viewMeta,
    }
  }

  const tables = [
    buildMonthlySocialPlatformTable(
      exportAllSources ? "Paid & organic" : "",
      exportAllSources ? "Combined paid and organic traffic" : "",
      columnDefs,
      dataRows,
      comparePeriod,
      connectedKeys,
    ),
  ].filter(Boolean)


  if (exportAllSources) {
    if (bySource.organic?.length) {
      const organicTable = buildMonthlySocialPlatformTable(
        "Organic only",
        "Paid & organic breakdown — organic traffic",
        columnDefs,
        bySource.organic,
        comparePeriod,
        connectedKeys,
      )
      if (organicTable) tables.push(organicTable)
    }
    if (bySource.paid?.length && monthlySocialRowsHaveMetricData(bySource.paid, columnDefs, connectedKeys)) {
      const paidTable = buildMonthlySocialPlatformTable(
        "Paid only",
        "Paid & organic breakdown — paid traffic",
        columnDefs,
        bySource.paid,
        comparePeriod,
        connectedKeys,
      )
      if (paidTable) tables.push(paidTable)
    }
  }

  return {
    description: MONTHLY_SOCIAL_DASHBOARD_SUBTITLE,
    viewMeta,
    summaryMetrics: [],
    tables,
    notes: [],
  }
}

function socialPerformanceBenchmarkExportUsesAllTabs(ctx) {
  return (ctx.exportAllDataChartIds || []).includes("socialPerformanceBenchmark")
}

const SOCIAL_BENCHMARK_DASHBOARD_SUBTITLE =
  "View how you compare against competitors and your brand's average property. Data is sourced from public, organic performance."

const SOCIAL_BENCHMARK_REPORT_TITLE = "Social Performance: You vs Competitors"
const SOCIAL_BENCHMARK_ENGAGEMENT_HEADING = "Engagement"
const SOCIAL_BENCHMARK_FOLLOWER_HEADING = "Follower Growth"

function buildSocialPerformanceBenchmarkTable(tabKey, tabData) {
  const rows = tabData?.rows ?? []
  if (!rows.length) return null
  const metricCols = tabData.columnLabels || []
  const columns = ["Rank", "Competitors", ...metricCols]
  const columnTypes = [
    "text",
    "competitor",
    ...metricCols.map((_, index) => (index === 0 ? "growth" : "text")),
  ]
  const tableRows = rows.map((row) => {
    const metrics =
      tabKey === "growth"
        ? [row.followerGrowth, row.newFollowers, row.totalFollowers]
        : [row.engagementGrowth, row.newEngagements, row.engagementRate]
    return [
      `#${row.rank}`,
      {
        kind: "competitor",
        name: row.brand,
        subBrand: row.subBrand || "",
        isYou: Boolean(row.isYou),
        isBrandAverage: Boolean(row.isBrandAverage),
      },
      ...metrics.map((value) => (value == null || String(value).trim() === "" ? "—" : String(value))),
    ]
  })
  const title =
    tabKey === "growth" ? SOCIAL_BENCHMARK_FOLLOWER_HEADING : SOCIAL_BENCHMARK_ENGAGEMENT_HEADING
  return buildTable(title, "", columns, tableRows, columnTypes, {
    className: "report-table-wrap--competitors report-pdf-block",
    columnWeights: [0.7, 2.4, 1.2, 1.2, 1.2],
    insight: String(tabData?.strategicInsight || "").trim(),
  })
}

function buildCompetitiveBenchmarkContent(ctx) {
  const byTab = ctx.socialPerformanceByTab
  const hasApiRows =
    (byTab?.engagement?.rows?.length ?? 0) > 0 || (byTab?.growth?.rows?.length ?? 0) > 0

  if (hasApiRows) {
    const richHtml = buildSocialPerformanceBenchmarkRichHtml(ctx)
    if (!richHtml) {
      return {
        ...emptyContent(
          SOCIAL_BENCHMARK_DASHBOARD_SUBTITLE,
          "No competitive benchmark data for this property or date range.",
        ),
        reportTitle: SOCIAL_BENCHMARK_REPORT_TITLE,
      }
    }
    return {
      reportTitle: SOCIAL_BENCHMARK_REPORT_TITLE,
      description: "",
      summaryMetrics: [],
      richHtml,
      tables: [],
      notes: [],
    }
  }

  const daily = ctx.competitorDaily || []
  const chartData = ctx.competitorChartData || []
  if (!daily.length && !chartData.length) {
    return {
      ...emptyContent(
        SOCIAL_BENCHMARK_DASHBOARD_SUBTITLE,
        "No competitive benchmark data for this property or date range.",
      ),
      reportTitle: SOCIAL_BENCHMARK_REPORT_TITLE,
    }
  }
  const avg = ctx.competitorBenchmarkAvg || {}
  const summaryMetrics = [
    avg.you ? { label: "Your average", value: String(avg.you) } : null,
    avg.competitor ? { label: "Competitor average", value: String(avg.competitor) } : null,
    avg.delta ? { label: "Vs competitors", value: String(avg.delta) } : null,
  ].filter(Boolean)

  const tables = []
  if (chartData.length) {
    tables.push(
      buildTable(
        "You vs competitors (daily)",
        "Recent days in range",
        ["Date", "You", "Riviera", "Ocean", "Palm", "Sunset", "Marina"],
        chartData.map((row) => [
          row.day ?? row.date,
          row.you,
          row.riviera,
          row.ocean,
          row.palm,
          row.sunset,
          row.marina,
        ]),
        ["date", "number", "number", "number", "number", "number", "number"],
      ),
    )
  }

  return {
    reportTitle: SOCIAL_BENCHMARK_REPORT_TITLE,
    description: "",
    summaryMetrics,
    tables,
    notes: ctx.competitorBenchmarkInsight ? [ctx.competitorBenchmarkInsight] : [],
  }
}

const POST_PERFORMANCE_DASHBOARD_SUBTITLE =
  "Explore your highest- and lowest-performing posts by metric and platform."

function buildTopPostsContent(ctx) {
  const posts = Array.isArray(ctx.topPosts) ? ctx.topPosts : []
  if (!posts.length) {
    return {
      ...emptyContent(POST_PERFORMANCE_DASHBOARD_SUBTITLE, "No top posts in the selected range."),
    }
  }
  const exportAllPosts = (ctx.exportAllDataChartIds || []).includes("topPosts")
  const totalPosts = Number(ctx.topPostsTotalAvailable) || posts.length
  const viewMeta = exportAllPosts
    ? `${formatNumber(totalPosts)} post${totalPosts === 1 ? "" : "s"} total`
    : `Top ${formatNumber(posts.length)} posts`
  const engagementCell = (post) =>
    formatTopPostEngagementRate(post.engagementRate ?? post.engagement)

  return {
    description: exportAllPosts ? POST_PERFORMANCE_DASHBOARD_SUBTITLE : "",
    viewMeta,
    summaryMetrics: [],
    tables: [
      buildTable(
        "",
        "",
        [
          "Platform",
          "Post",
          "Date",
          "Eng. %",
          "Likes",
          "Comments",
          "Shares",
          "Saves",
          "Impr.",
          "Clicks",
          "Link",
        ],
        posts.map((post) => [
          exportRowPlatform(post),
          {
            title: String(post.topic ?? "").replace(/\s+/g, " ").trim(),
            copy: String(post.postText ?? "").replace(/\s+/g, " ").trim(),
          },
          formatTopPostDisplayDate(
            getTopPostDateValue(post),
            ctx.propertyChartTimezone,
          ),
          engagementCell(post),
          post.likes ?? 0,
          post.comments ?? 0,
          post.shares ?? 0,
          post.saves ?? 0,
          post.impressions ?? 0,
          post.postClicks ?? 0,
          post.permalink || post.postLink || "",
        ]),
        [
          "platformIcon",
          "postCaption",
          "text",
          "text",
          "number",
          "number",
          "number",
          "number",
          "number",
          "number",
          "link",
        ],
        {
          className: "report-table-wrap--dense report-table-wrap--posts",
          columnWeights: [0.55, 4.1, 0.95, 0.7, 0.65, 0.85, 0.65, 0.65, 0.8, 0.65, 0.65],
        },
      ),
    ],
    notes: [],
  }
}

const TOP_STORY_DASHBOARD_SUBTITLE =
  "Stories ranked by impressions, reach, replies, and retention."

function buildTopStoriesContent(ctx) {
  const stories = Array.isArray(ctx.topStories) ? ctx.topStories : []
  const rangeLabel = String(ctx.paidMediaRangeLabel || "").trim()
  const totalStories = Number(ctx.topStoriesTotalAvailable)
  const storiesLabel =
    Number.isFinite(totalStories) && totalStories > stories.length
      ? `${stories.length} of ${totalStories} ${totalStories === 1 ? "story" : "stories"}`
      : `${stories.length} ${stories.length === 1 ? "story" : "stories"}`
  const viewMeta = [
    storiesLabel,
    rangeLabel,
  ]
    .filter(Boolean)
    .join(" · ")
  if (!stories.length) {
    return {
      ...emptyContent(TOP_STORY_DASHBOARD_SUBTITLE, "No stories in the selected range."),
      viewMeta: rangeLabel,
    }
  }

  return {
    description: TOP_STORY_DASHBOARD_SUBTITLE,
    viewMeta,
    summaryMetrics: [],
    tables: [
      buildTable(
        "",
        "",
        [
          "",
          "Story",
          "Date",
          "Stories",
          "Impr.",
          "Reach",
          "Replies",
          "Retention",
        ],
        stories.map((story) => [
          exportRowPlatform(story),
          `${story.storyCount} ${story.storyCount === 1 ? "story" : "stories"}`,
          story.date,
          story.storyCount ?? 0,
          story.impressions ?? 0,
          story.reach ?? 0,
          story.replies ?? 0,
          `${Number(story.retentionRate) || 0}%`,
        ]),
        [
          "platformIcon",
          "text",
          "date",
          "number",
          "number",
          "number",
          "number",
          "text",
        ],
        {
          className: "report-table-wrap--dense report-table-wrap--posts report-table-wrap--stories",
          columnWeights: [0.45, 1.15, 0.75, 0.55, 0.75, 0.7, 0.6, 0.7],
        },
      ),
    ],
    notes: [],
  }
}

function buildOptimalPostingContent(ctx) {
  const heat = ctx.postHeatmap
  const weekly = heat?.weeklyData
  const wMatrix = weekly?.matrix
  const wSamples = weekly?.samples
  const quartiles =
    Array.isArray(wMatrix) && Array.isArray(wSamples)
      ? getHeatmapQuartiles(wMatrix, wSamples, { minSamples: 1 })
      : { q1: 0, q3: 0 }
  const opportunities =
    Array.isArray(wMatrix) && Array.isArray(wSamples)
      ? deriveWeekdayPostingOpportunities(wMatrix, wSamples, quartiles, {
          minSamples: 1,
          engagements: weekly?.engagements,
          totalEngagements: weekly?.totalEngagements,
        })
      : null

  const opportunityRows = [
    ["Best opportunity", opportunities?.bestPerforming],
    ["Most reliable", opportunities?.consistentPerformer],
    ["Needs review", opportunities?.improvementOpportunity],
  ]
    .filter(([, item]) => item?.timeWindow)
    .map(([label, item]) => {
      const share = Number(item.shareOfTotalEngagement)
      return [
        label,
        item.timeWindow,
        item.postCount ?? 0,
        Number.isFinite(share) ? `${share.toFixed(1)}%` : "—",
      ]
    })

  const posts = Number(weekly?.totalPostsUsed) || 0
  const engagements = weekly?.totalEngagements
  const viewMeta = [
    posts ? `${formatNumber(posts)} posts published` : "",
    engagements != null && Number.isFinite(Number(engagements))
      ? `${formatNumber(engagements)} engagements`
      : "",
  ].filter(Boolean).join(" · ")

  if (!opportunityRows.length && !rankedSlotsFromPostHeatmap(heat).length) {
    return {
      ...emptyContent(
        OPTIMAL_POSTING_WIDGET_SUBTITLE,
        "Open Content Analytics to load posting-time data, or no data for this range.",
      ),
      reportTitle: "Content Analytics",
    }
  }

  const tables = []
  if (opportunityRows.length) {
    tables.push(
      buildTable(
        "Posting time insights",
        "Same windows as the dashboard: best opportunity, most reliable, and needs review.",
        ["Signal", "Window", "Posts", "Share of total engagement"],
        opportunityRows,
        ["text", "text", "number", "text"],
        { columnWeights: [1.4, 2.2, 0.8, 1.2] },
      ),
    )
  }

  return {
    reportTitle: "Content Analytics",
    description: OPTIMAL_POSTING_WIDGET_SUBTITLE,
    viewMeta,
    summaryMetrics: [],
    tables,
    notes: [],
  }
}

function buildPaidMediaPerformanceContent(ctx) {
  const totals = ctx.paidMediaTotals || {}
  const series = Array.isArray(ctx.paidMediaSeries) ? ctx.paidMediaSeries : []
  const rangeLabel = String(ctx.paidMediaRangeLabel || ctx.exportRange || "").trim()
  const subtitle = rangeLabel
    ? `Visualize revenue generated vs spend for Meta campaigns managed through SoHo · ${rangeLabel}`
    : "Visualize revenue generated vs spend for Meta campaigns managed through SoHo."
  const hasTotals =
    Number(totals.spend) > 0 ||
    Number(totals.revenue) > 0 ||
    Number(totals.roas) > 0
  const hasSeriesActivity = series.some(
    (row) =>
      Number(row.spend) > 0 || Number(row.revenue) > 0 || Number(row.roas) > 0,
  )
  if (!hasTotals && !hasSeriesActivity) {
    return {
      ...emptyContent(subtitle, "No paid media spend or revenue in the selected range."),
      reportTitle: "Paid Media Performance",
    }
  }
  const delta = Number(ctx.paidMediaRevenueDelta)
  const deltaHint = Number.isFinite(delta)
    ? `${delta > 0 ? "+" : ""}${delta}% vs previous period`
    : ""
  const tables = []
  if (series.length) {
    tables.push(
      buildTable(
        "Spend vs revenue trend",
        rangeLabel ? `Daily totals · ${rangeLabel}` : "Daily totals in the reporting window",
        ["Date", "Spend", "Revenue", "ROAS"],
        series.map((row) => [
          row.date ?? row.label,
          row.spend,
          row.revenue,
          `${Number(row.roas ?? 0).toFixed(1)}x`,
        ]),
        ["date", "currency", "currency", "text"],
        { columnWeights: [1.1, 1, 1, 0.8] },
      ),
    )
  }
  return {
    reportTitle: "Paid Media Performance",
    description: subtitle,
    viewMeta: rangeLabel,
    summaryMetrics: [
      {
        label: "Total spend",
        value: `$${formatCompactNumber(totals.spend)}`,
        hint: rangeLabel || undefined,
      },
      {
        label: "Revenue generated",
        value: `$${formatCompactNumber(totals.revenue)}`,
        hint: deltaHint || undefined,
      },
      {
        label: "Average ROAS",
        value: `${Number(totals.roas || 0).toFixed(1)}x`,
        hint: "Return on ad spend",
      },
    ],
    tables,
    notes: [],
  }
}

function formatPaidRoas(value) {
  return `${Number(value || 0).toFixed(1)}x`
}

function formatPaidMoney(value) {
  return `$${formatCompactNumber(value || 0)}`
}

const PAID_PERFORMANCE_EXPORT_WEIGHT_BY_KEY = {
  objective: 0.95,
  campaignName: 1.7,
  platform: 0.38,
  format: 0.58,
  impressions: 0.78,
  reach: 0.62,
  linkClicks: 0.7,
  cpc: 0.58,
  postEngagement: 0.82,
  cpe: 0.52,
  engagementRate: 0.82,
  newFollowers: 0.72,
  shares: 0.55,
  saves: 0.52,
  videoViews: 0.68,
  spend: 0.62,
  roas: 0.52,
  revenue: 0.68,
  purchases: 0.7,
}

function paidPerformanceExportHeader(column) {
  if (column.key === "platform") return ""
  if (column.key === "campaignName") return "Campaign\nName"
  if (column.key === "linkClicks") return "Link\nClicks"
  if (column.key === "postEngagement") return "Post\nEngagement"
  if (column.key === "engagementRate") return "Engagement\nRate"
  if (column.key === "newFollowers") return "New\nFollowers"
  if (column.key === "videoViews") return "Video\nViews"
  return column.label
}

const PAID_PERFORMANCE_EXPORT_COLUMNS = TABLE_COLUMNS.map(paidPerformanceExportHeader)

const PAID_PERFORMANCE_EXPORT_TYPES = TABLE_COLUMNS.map((column) =>
  column.key === "platform" ? "platformIcon" : "text",
)

const PAID_PERFORMANCE_EXPORT_WEIGHTS = TABLE_COLUMNS.map(
  (column) => PAID_PERFORMANCE_EXPORT_WEIGHT_BY_KEY[column.key] || 0.8,
)

function resolvePaidAdvertisingPerformanceRows(ctx) {
  const exportAllData = (ctx.exportAllDataChartIds || []).includes("topPerformingAds")

  if (!exportAllData) {
    if (Array.isArray(ctx.paidMediaPerformanceCampaigns) && ctx.paidMediaPerformanceCampaigns.length) {
      return ctx.paidMediaPerformanceCampaigns
    }
  }

  const rawCampaigns = Array.isArray(ctx.paidMediaRawCampaigns) ? ctx.paidMediaRawCampaigns : []
  const rawAds = Array.isArray(ctx.paidMediaRawAds) ? ctx.paidMediaRawAds : []
  if (rawCampaigns.length) {
    return mapPaidPerformanceCampaigns(rawCampaigns, rawAds, { keepAllRows: true })
  }

  if (Array.isArray(ctx.paidMediaPerformanceCampaigns) && ctx.paidMediaPerformanceCampaigns.length) {
    return ctx.paidMediaPerformanceCampaigns
  }
  if (Array.isArray(ctx.paidMediaCampaigns) && ctx.paidMediaCampaigns.length) {
    return ctx.paidMediaCampaigns
  }
  return Array.isArray(ctx.paidMediaTopAds) ? ctx.paidMediaTopAds : []
}

function resolvePaidCampaignExportRows(ctx) {
  const exportAllData = (ctx.exportAllDataChartIds || []).includes("topPerformingAds")
  const mappedCampaigns = Array.isArray(ctx.paidMediaCampaigns) ? ctx.paidMediaCampaigns : []
  const mapCampaignRow = (row) => ({
    name: row.campaignName || row.name || row.title || "—",
    objective: row.objective,
    adCount: row.adCount ?? 0,
    roas: row.roas,
    revenue: row.revenue,
    spend: row.spend,
  })

  if (!exportAllData && mappedCampaigns.length) {
    return mappedCampaigns.map(mapCampaignRow)
  }

  const rawCampaigns = Array.isArray(ctx.paidMediaRawCampaigns) ? ctx.paidMediaRawCampaigns : []
  if (rawCampaigns.length) {
    return paidCampaignRowsToSummaries(rawCampaigns)
  }
  if (mappedCampaigns.length) {
    return mappedCampaigns.map(mapCampaignRow)
  }
  const ads = Array.isArray(ctx.paidMediaTopAds) ? ctx.paidMediaTopAds : []
  return buildPaidCampaignSummaries(ads, { campaigns: rawCampaigns })
}

function resolvePaidObjectiveExportRows(ctx) {
  const rawObjectives = Array.isArray(ctx.paidMediaObjectives) ? ctx.paidMediaObjectives : []
  if (rawObjectives.length) {
    return paidObjectiveRowsToSummaries(rawObjectives)
  }
  const ads = Array.isArray(ctx.paidMediaTopAds) ? ctx.paidMediaTopAds : []
  const rawCampaigns = Array.isArray(ctx.paidMediaRawCampaigns) ? ctx.paidMediaRawCampaigns : []
  return buildPaidObjectiveSummaries({
    ads,
    campaigns: rawCampaigns,
  }).filter(Boolean)
}

function buildAdvertisingPerformanceExportRow(row) {
  return TABLE_COLUMNS.map((column) => {
    if (column.key === "platform") return exportRowPlatform(row)
    return formatTableCell(row, column, {
      formatCompactNumber,
      platformLabels: {},
    })
  })
}

function sortPaidPerformanceTableRows(rows, sortBy = "impressions", sortDir = "desc") {
  const column = TABLE_COLUMNS.find((entry) => entry.key === sortBy) || TABLE_COLUMNS[4]
  return [...rows].sort((a, b) => {
    const cmp = compareValues(a[column.key], b[column.key], column.sortType)
    return sortDir === "desc" ? -cmp : cmp
  })
}

function resolvePaidAdsExportChartId(chartId, ctx) {
  if (chartId !== "topPerformingAds" && chartId !== "topPaidAds") return chartId
  const exportAllTabs = (ctx.exportAllDataChartIds || []).includes("topPerformingAds")
  if (exportAllTabs) return chartId
  const view = ctx.paidWidgetView
  if (view === "performance") return "advertisingPerformance"
  if (view === "campaigns") return "topCampaigns"
  if (view === "objectives") return "topObjectives"
  return chartId
}

function buildAdvertisingPerformanceExportTable(rows, title, subtitle, sortOptions = {}) {
  const sorted = sortPaidPerformanceTableRows(
    rows,
    sortOptions.sortBy || "impressions",
    sortOptions.sortDir || "desc",
  )
  const countLabel = sorted.length === 1 ? "campaign" : "campaigns"
  const tableSubtitle =
    subtitle ||
    `${formatNumber(sorted.length)} ${countLabel} · detailed campaign performance by objective`
  return buildTable(
    title,
    tableSubtitle,
    PAID_PERFORMANCE_EXPORT_COLUMNS,
    sorted.map((row) => buildAdvertisingPerformanceExportRow(row)),
    PAID_PERFORMANCE_EXPORT_TYPES,
    {
      className: "report-table-wrap--dense report-table-wrap--paid-ads",
      columnWeights: PAID_PERFORMANCE_EXPORT_WEIGHTS,
    },
  )
}

function paidApiCountSubtitle(count, noun) {
  const label = count === 1 ? noun : `${noun}s`
  return `${formatNumber(count)} ${label} · metrics from paid performance API`
}

function buildTopPerformingAdsExportTable(sortedAds) {
  return buildTable(
    "Top Performing Ads",
    `${formatNumber(sortedAds.length)} ads · metrics from paid performance API`,
    ["Ad", "Format", "ROAS", "Revenue", "Spend"],
    sortedAds.map((ad) => [
      ad.title || ad.name || ad.adName || "—",
      ad.format || "Image",
      formatPaidRoas(ad.roas),
      formatPaidMoney(ad.revenue),
      formatPaidMoney(ad.spend),
    ]),
    ["text", "text", "text", "text", "text"],
    {
      className: "report-table-wrap--dense report-table-wrap--paid-ads",
      columnWeights: [2.6, 1, 0.8, 1, 1],
    },
  )
}

function buildTopCampaignsExportTable(campaigns) {
  return buildTable(
    "Top campaigns",
    paidApiCountSubtitle(campaigns.length, "campaign"),
    ["Campaign", "Objective", "Ads", "ROAS", "Revenue", "Spend"],
    campaigns.map((c) => [
      c.name,
      c.objective || "—",
      c.adCount,
      formatPaidRoas(c.roas),
      formatPaidMoney(c.revenue),
      formatPaidMoney(c.spend),
    ]),
    ["text", "text", "number", "text", "text", "text"],
    {
      className: "report-table-wrap--dense report-table-wrap--paid-ads",
      columnWeights: [2.6, 1.3, 0.7, 0.8, 1, 1],
    },
  )
}

function buildTopObjectivesExportTable(objectives) {
  return buildTable(
    "Top objectives",
    paidApiCountSubtitle(objectives.length, "objective"),
    ["Objective", "Campaigns", "Ads", "ROAS", "Revenue", "Spend"],
    objectives.map((o) => [
      o.name,
      o.campaignCount,
      o.adCount,
      formatPaidRoas(o.roas),
      formatPaidMoney(o.revenue),
      formatPaidMoney(o.spend),
    ]),
    ["text", "number", "number", "text", "text", "text"],
    {
      className: "report-table-wrap--dense report-table-wrap--paid-ads",
      columnWeights: [2.2, 1.1, 0.8, 0.8, 1, 1],
    },
  )
}

function buildPaidMediaAllDataContent(ctx) {
  const ads = Array.isArray(ctx.paidMediaTopAds) ? ctx.paidMediaTopAds : []
  const cardSortBy = ctx.paidWidgetSortBy || "roas"
  const cardSortDir = ctx.paidWidgetSortDir || "desc"
  const tableSort = {
    sortBy: ctx.paidPerformanceTableSortBy || "impressions",
    sortDir: ctx.paidPerformanceTableSortDir || "desc",
  }
  const tables = []

  if (ads.length) {
    tables.push(buildTopPerformingAdsExportTable(sortByMetric(ads, cardSortBy, cardSortDir)))
  }

  const performanceRows = resolvePaidAdvertisingPerformanceRows(ctx)
  if (performanceRows.length) {
    tables.push(
      buildAdvertisingPerformanceExportTable(
        performanceRows,
        "Advertising Performance",
        "Detailed campaign performance by objective.",
        tableSort,
      ),
    )
  }

  if (!tables.length) {
    return emptyContent("Top Performing Ads", "No paid media data in the selected range.")
  }

  return {
    reportTitle: "Top Performing Ads",
    description: "Highest ROAS ads by selected objective and platform.",
    summaryMetrics: [],
    tables,
    notes: [],
  }
}

function buildPaidAdsWidgetContent(ctx, chartId) {
  const exportAllPaidTabs = (ctx.exportAllDataChartIds || []).includes("topPerformingAds")
  if (exportAllPaidTabs && (chartId === "topPerformingAds" || chartId === "topPaidAds")) {
    return buildPaidMediaAllDataContent(ctx)
  }

  const resolvedChartId = resolvePaidAdsExportChartId(chartId, ctx)
  const ads = Array.isArray(ctx.paidMediaTopAds) ? ctx.paidMediaTopAds : []
  const performanceRows = resolvePaidAdvertisingPerformanceRows(ctx)
  const cardSortBy = ctx.paidWidgetSortBy || "roas"
  const cardSortDir = ctx.paidWidgetSortDir || "desc"
  const tableSort = {
    sortBy: ctx.paidPerformanceTableSortBy || "impressions",
    sortDir: ctx.paidPerformanceTableSortDir || "desc",
  }

  if (resolvedChartId === "topCampaigns") {
    const campaigns = sortByMetric(
      resolvePaidCampaignExportRows(ctx),
      cardSortBy,
      cardSortDir,
    )
    if (!campaigns.length) {
      return emptyContent("Top campaigns", "No campaigns in the selected range.")
    }
    const campaignTable = buildTopCampaignsExportTable(campaigns)
    if (!campaignTable) {
      return emptyContent("Top campaigns", "No campaigns in the selected range.")
    }
    return {
      reportTitle: "Top campaigns",
      description: "",
      summaryMetrics: [],
      tables: [campaignTable],
      notes: [],
    }
  }

  if (resolvedChartId === "topObjectives") {
    const objectives = sortByMetric(
      resolvePaidObjectiveExportRows(ctx),
      cardSortBy,
      cardSortDir,
    )
    return {
      reportTitle: "Top objectives",
      description: "",
      summaryMetrics: [],
      tables: [buildTopObjectivesExportTable(objectives)],
      notes: [],
    }
  }

  if (resolvedChartId === "advertisingPerformance") {
    if (!performanceRows.length) {
      return emptyContent(
        "Advertising Performance",
        "No campaign performance data available.",
      )
    }
    return {
      reportTitle: "Advertising Performance",
      description: "Detailed campaign performance by objective.",
      summaryMetrics: [],
      tables: [
        buildAdvertisingPerformanceExportTable(
          performanceRows,
          "Advertising Performance",
          "Detailed campaign performance by objective.",
          tableSort,
        ),
      ],
      notes: [],
    }
  }

  if (resolvedChartId === "topPerformingAds" || resolvedChartId === "topPaidAds") {
    if (!ads.length) {
      return emptyContent("Top Performing Ads", "No paid ads in the selected range.")
    }

    const sortedAds = sortByMetric(ads, cardSortBy, cardSortDir)
    return {
      reportTitle: "Top Performing Ads",
      description: "Highest ROAS ads by selected objective and platform.",
      summaryMetrics: [],
      tables: [buildTopPerformingAdsExportTable(sortedAds)],
      notes: [],
    }
  }

  return emptyContent("Top Performing Ads", "No paid ads in the selected range.")
}

function audienceBundleInsight(ctx, kind) {
  switch (kind) {
    case "age":
      return (
        String(ctx.audienceAgeGenderSummary || "").trim() ||
        "Review age and gender breakdown to tailor content and targeting."
      )
    case "cities":
      return String(ctx.audienceTopCitiesInsight || "").trim()
    case "countries":
      return String(ctx.audienceTopCountriesInsight || "").trim()
    default:
      return ""
  }
}

function buildAudienceContent(ctx) {
  if (!audienceDemographicsHaveData(ctx)) {
    return emptyContent(
      "Audience Insights",
      "No audience demographics for the selected property.",
    )
  }
  const age = ctx.exportAudienceAgeGender ?? ctx.audienceAgeGender ?? []
  const cities = ctx.exportAudienceTopCities ?? ctx.audienceTopCities ?? []
  const countries = ctx.exportAudienceTopCountries ?? ctx.audienceTopCountries ?? []
  const geographic = Array.isArray(ctx.audienceGeographic) ? ctx.audienceGeographic : []
  const ageSource =
    String(ctx.audienceAgeGenderSourceLabel || "").trim() ||
    "Age and Gender data from Facebook"
  const tableClass = "report-table-wrap--audience"
  const tables = []

  const ageRowsWithData = age.filter(audienceAgeRowHasData)
  if (ageRowsWithData.length) {
    const hasGenderSplit = ageRowsWithData.some((r) => r.female != null || r.male != null)
    tables.push(
      hasGenderSplit
        ? buildTable(
            "",
            "",
            ["Age", "Female", "Male", "Total"],
            ageRowsWithData.map((row) => {
              const female = Number(row.female) || 0
              const male = Number(row.male) || 0
              return [row.age ?? row.label, female, male, female + male]
            }),
            ["text", "compact", "compact", "compact"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              columnWeights: [1.2, 1, 1, 1],
              bundleTitle: "Age & Gender Distribution",
              bundleSubtitle: ageSource,
              insight: audienceBundleInsight(ctx, "age"),
            },
          )
        : buildTable(
            "",
            "",
            ["Segment", "Share"],
            ageRowsWithData.map((row) => [row.label ?? row.segment, row.value ?? row.share]),
            ["text", "percent"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              bundleTitle: "Age & Gender Distribution",
              bundleSubtitle: ageSource,
              insight: audienceBundleInsight(ctx, "age"),
            },
          ),
    )
  }

  const cityRows = cities.filter(audiencePlaceRowHasData).slice(0, AUDIENCE_EXPORT_MAX_PLACE_ROWS)
  if (cityRows.length) {
    const hasSplit = cityRows.some((row) => {
      const parts = audiencePlatformFollowers(row)
      return parts.facebook > 0 || parts.instagram > 0
    })
    tables.push(
      hasSplit
        ? buildTable(
            "",
            "",
            ["City", "Facebook", "Instagram", "Total"],
            cityRows.map((row) => {
              const parts = audiencePlatformFollowers(row)
              return [row.city ?? row.label, parts.facebook, parts.instagram, parts.total]
            }),
            ["text", "compact", "compact", "compact"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              columnWeights: [1.8, 1.1, 1.1, 1],
              bundleTitle: "Top Cities",
              bundleSubtitle: "Hometown city data from Facebook and Instagram",
              insight: audienceBundleInsight(ctx, "cities"),
            },
          )
        : buildTable(
            "",
            "",
            ["City", "Followers"],
            cityRows.map((row) => [row.city ?? row.label, audiencePlatformFollowers(row).total]),
            ["text", "compact"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              bundleTitle: "Top Cities",
              bundleSubtitle: "Hometown city data from Facebook and Instagram",
              insight: audienceBundleInsight(ctx, "cities"),
            },
          ),
    )
  }

  const countryRows = countries.filter(audiencePlaceRowHasData).slice(0, AUDIENCE_EXPORT_MAX_PLACE_ROWS)
  if (countryRows.length) {
    const hasSplit = countryRows.some((row) => {
      const parts = audiencePlatformFollowers(row)
      return parts.facebook > 0 || parts.instagram > 0
    })
    tables.push(
      hasSplit
        ? buildTable(
            "",
            "",
            ["Country", "Facebook", "Instagram", "Total"],
            countryRows.map((row) => {
              const parts = audiencePlatformFollowers(row)
              return [row.country ?? row.label ?? row.name, parts.facebook, parts.instagram, parts.total]
            }),
            ["text", "compact", "compact", "compact"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              columnWeights: [1.8, 1.1, 1.1, 1],
              bundleTitle: "Top Countries by Platform",
              bundleSubtitle: "Hometown country data from Facebook and Instagram",
              insight: audienceBundleInsight(ctx, "countries"),
            },
          )
        : buildTable(
            "",
            "",
            ["Country", "Followers"],
            countryRows.map((row) => [
              row.country ?? row.label ?? row.name,
              audiencePlatformFollowers(row).total,
            ]),
            ["text", "compact"],
            {
              ...AUDIENCE_SIDE_BY_SIDE_TABLE_OPTIONS,
              bundleTitle: "Top Countries by Platform",
              bundleSubtitle: "Hometown country data from Facebook and Instagram",
              insight: audienceBundleInsight(ctx, "countries"),
            },
          ),
    )
  }

  const geoRows = geographic.filter((row) => geographicFollowerCount(row) > 0)
  if (geoRows.length) {
    const geoTotal = geoRows.reduce((sum, row) => sum + geographicFollowerCount(row), 0)
    tables.push(
      buildTable(
        "Geographic Distribution",
        "Audience location breakdown by country",
        ["Country", "Total Followers", "Follower %"],
        geoRows.map((row) => {
          const count = geographicFollowerCount(row)
          const code = String(row.code || "").trim()
          const name = String(row.country || row.label || "").trim()
          return [
            code && name ? `${code} ${name}` : name || code || "—",
            count,
            formatDemographicsFollowerShare(count, geoTotal),
          ]
        }),
        ["text", "number", "text"],
        {
          className: `${tableClass} report-table-wrap--geographic`,
          columnWeights: [2.2, 1.2, 1],
          insight: ctx.audienceGeographicInsight || "",
        },
      ),
    )
  }

  const built = tables.filter(Boolean)
  if (!built.length) {
    return emptyContent(
      "Audience Insights",
      "No audience demographics for the selected property.",
    )
  }
  return {
    reportTitle: "Audience Insights",
    description: "",
    summaryMetrics: [],
    tables: built,
    notes: [],
  }
}

function buildMessagesContent(ctx) {
  const series = sliceSeriesForReport(ctx.monitoringSeries || [], 14)
  const total =
    Number(ctx.totalMonitoringShare) ||
    sumField(series, "messages") ||
    sumField(series, "value")
  if (!series.length || total === 0) {
    return emptyContent("Inbound message volume.", "No messages in this date range.")
  }
  return {
    reportTitle: "Messages",
    description: "Community activity read by the Monitoring team",
    summaryMetrics: [
      { label: "Messages (shown days)", value: formatCompactNumber(total) },
      { label: "Days in table", value: formatNumber(series.length) },
    ],
    tables: [
      buildTable(
        "Daily message volume",
        "Recent days in range",
        ["Date", "Messages"],
        series.map((row) => [row.date ?? row.label, row.messages ?? row.value]),
        ["date", "number"],
      ),
    ],
    notes: [],
  }
}

function buildSentimentContent(ctx) {
  if (!sentimentMonitoringHasData(ctx)) {
    return emptyContent("Message sentiment mix.", "No sentiment data in this date range.")
  }
  const series = sliceSeriesForReport(ctx.sentimentSeries || [], 14)
  const last = series[series.length - 1]
  return {
    reportTitle: "Message Sentiment",
    description: "AI-powered sentiment categorization",
    summaryMetrics: [
      { label: "Latest positive", value: formatNumber(last?.positive ?? 0) },
      { label: "Latest negative", value: formatNumber(last?.negative ?? 0) },
    ],
    tables: [
      buildTable(
        "Daily sentiment counts",
        "Recent days in range",
        ["Date", "Positive", "Neutral", "Negative"],
        series.map((row) => [
          row.date ?? row.label,
          row.positive,
          row.neutral,
          row.negative,
        ]),
        ["date", "number", "number", "number"],
      ),
    ],
    notes: [],
  }
}

function sumAlertRowVolume(row, seriesKeys = null) {
  if (!row || typeof row !== "object") return 0
  if (row.alerts != null || row.value != null) {
    return Number(row.alerts ?? row.value) || 0
  }
  const keys = seriesKeys?.length
    ? seriesKeys
    : Object.keys(row).filter((key) => key !== "date" && key !== "label")
  return keys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0)
}

function aggregateAlertVolumeByDate(chartRows, series = []) {
  const seriesKeys = (series || []).map((item) => item.key).filter(Boolean)
  const map = new Map()
  for (const row of chartRows) {
    const date = row.date ?? row.label
    if (!date) continue
    const alerts = sumAlertRowVolume(row, seriesKeys)
    map.set(date, (map.get(date) || 0) + alerts)
  }
  return [...map.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    .map(([date, alerts]) => ({ date, alerts }))
}

export function alertsMonitoringHasData(ctx = {}) {
  const total = Number(ctx.totalAlerts) || 0
  const categoryTotal = (ctx.alertCategories || []).reduce(
    (sum, item) => sum + (Number(item.count) || 0),
    0,
  )
  const { rows, series } = normalizeAlertsDailyRows(ctx)
  const dailyTotal = rows.reduce(
    (sum, row) => sum + sumAlertRowVolume(row, series.map((item) => item.key)),
    0,
  )
  const sparseTotal = (ctx.alertsSeries || []).reduce(
    (sum, row) => sum + (Number(row.value ?? row.alerts) || 0),
    0,
  )
  return total > 0 || categoryTotal > 0 || dailyTotal > 0 || sparseTotal > 0
}

function buildAlertsContent(ctx) {
  if (!alertsMonitoringHasData(ctx)) {
    return emptyContent("Property alerts volume.", "No alerts recorded in this date range.")
  }

  const categoryTotal = (ctx.alertCategories || []).reduce(
    (sum, item) => sum + (Number(item.count) || 0),
    0,
  )
  const total = Number(ctx.totalAlerts) || categoryTotal
  const { rows: alertRows, series: alertSeries } = normalizeAlertsDailyRows(ctx)
  const aggregatedFromChart = alertRows.length
    ? aggregateAlertVolumeByDate(alertRows, alertSeries)
    : []
  const series =
    aggregatedFromChart.length > 0
      ? sliceSeriesForReport(aggregatedFromChart, 14)
      : sliceSeriesForReport(ctx.alertsSeries || [], 14)
  const nonZeroSeries = series.filter((row) => Number(row.alerts ?? row.value) > 0)
  const effectiveTotal =
    total > 0
      ? total
      : sumField(nonZeroSeries, "alerts") || sumField(nonZeroSeries, "value")

  const summaryMetrics = [
    { label: "Total alerts", value: formatNumber(effectiveTotal) },
    ...(ctx.alertCategories || [])
      .filter((item) => Number(item.count) > 0)
      .slice(0, 5)
      .map((item) => ({
        label: item.name,
        value: formatNumber(item.count ?? 0),
      })),
  ]

  const tables = []
  if (nonZeroSeries.length) {
    tables.push(
      buildTable(
        "Daily alert volume",
        "Days with alert activity in range",
        ["Date", "Alerts"],
        nonZeroSeries.map((row) => [row.date ?? row.label, row.alerts ?? row.value]),
        ["date", "number"],
      ),
    )
  }

  return {
    reportTitle: "Alerts",
    description: "Customer service situations emailed to on-property staff",
    summaryMetrics,
    tables,
    notes: [],
  }
}

function buildTimeOfDayContent(ctx) {
  const msg = (ctx.timeOfDayMessages || []).filter((e) => Number(e.value) > 0)
  const alerts = (ctx.timeOfDayAlerts || []).filter((e) => Number(e.value) > 0)
  if (!msg.length && !alerts.length) {
    return emptyContent(
      "Activity by time of day.",
      "No message or alert activity in this date range.",
    )
  }
  const peakMsg = msg.length
    ? [...msg].sort((a, b) => Number(b.value) - Number(a.value))[0]
    : null
  const peakAlert = alerts.length
    ? [...alerts].sort((a, b) => Number(b.value) - Number(a.value))[0]
    : null
  const summaryMetrics = []
  if (peakMsg) {
    summaryMetrics.push({
      label: "Peak messages",
      value: `${peakMsg.name ?? peakMsg.label} (${formatPercent(peakMsg.value, 0)})`,
    })
  }
  if (peakAlert) {
    summaryMetrics.push({
      label: "Peak alerts",
      value: `${peakAlert.name ?? peakAlert.label} (${formatPercent(peakAlert.value, 0)})`,
    })
  }
  const tables = []
  if (msg.length) {
    tables.push(
      buildTable(
        "Messages by time slot",
        "Share of message volume",
        ["Time slot", "Share"],
        msg.map((row) => [row.name ?? row.label, row.value]),
        ["text", "percent"],
      ),
    )
  }
  if (alerts.length) {
    tables.push(
      buildTable(
        "Alerts by time slot",
        "Share of alert volume",
        ["Time slot", "Share"],
        alerts.map((row) => [row.name ?? row.label, row.value]),
        ["text", "percent"],
      ),
    )
  }
  return {
    reportTitle: "Time of Day",
    description: "When guests message you and when alerts peak during the day.",
    summaryMetrics,
    tables,
    notes: [],
  }
}

function formatHeatmapPeakHour(value) {
  const fromLabel = parseHourLabel12ToIndex(value)
  if (fromLabel != null) return formatHourShort(fromLabel)
  const n = Number(value)
  if (Number.isFinite(n) && n >= 0 && n <= 23) return formatHourShort(n)
  return formatReportHour(value)
}

function buildMessageHeatmapContent(ctx) {
  const days =
    ctx.messageHeatmapWeeklyDays?.length
      ? ctx.messageHeatmapWeeklyDays
      : ctx.messageHeatmapDays || []
  const hours =
    ctx.messageHeatmapWeeklyHours?.length
      ? ctx.messageHeatmapWeeklyHours
      : ctx.messageHeatmapHours || []
  const matrix =
    ctx.messageHeatmapWeeklyValues?.length
      ? ctx.messageHeatmapWeeklyValues
      : ctx.messageHeatmapValues || []
  const slots = []
  if (Array.isArray(matrix[0])) {
    // Weekly matrix is hour rows × day columns (same as the dashboard heatmap).
    matrix.forEach((row, hourIndex) => {
      if (!Array.isArray(row)) return
      row.forEach((value, dayIndex) => {
        slots.push({
          day: days[dayIndex] ?? dayIndex,
          hour: hours[hourIndex] ?? hourIndex,
          value: Number(value) || 0,
        })
      })
    })
  }
  const top = slots.sort((a, b) => b.value - a.value).slice(0, 15)
  if (!top.length || top.every((s) => s.value <= 0)) {
    return emptyContent("Message heatmap.", "No heatmap data for this range.")
  }
  const peakDay = String(top[0].day ?? "").slice(0, 3)
  const peakHour = formatHeatmapPeakHour(top[0].hour)
  const calN = Array.isArray(ctx.messageHeatmapCalendarDates)
    ? ctx.messageHeatmapCalendarDates.length
    : 0
  const allMessages = slots.reduce((sum, s) => sum + (Number(s.value) || 0), 0)
  return {
    reportTitle: "Message Heatmap",
    description: peakDay && peakHour
      ? `Peak message times by day of week · Peak: ${peakDay} ${peakHour}`
      : "Peak message times by day of week",
    viewMeta: calN > 0
      ? `${formatCompactNumber(allMessages)} messages (${calN}d)`
      : `${formatCompactNumber(allMessages)} messages`,
    summaryMetrics: [
      { label: "Messages", value: formatCompactNumber(allMessages) },
    ],
    tables: [],
    notes: [],
  }
}

function buildGoalsContent(ctx) {
  const goals = ctx.activeGoals || []
  if (!goals.length) {
    return emptyContent("Social media goals.", "No active goals configured.")
  }
  const summary = ctx.goalsStatusSummary || {}
  const onTrackCount = (summary["on-track"] || 0) + (summary.exceeded || 0)
  const atRiskCount = (summary.behind || 0) + (summary["at-risk"] || 0)
  const periodHint = [
    ctx.goalsTimeRemainingText ? `Time remaining: ${ctx.goalsTimeRemainingText}` : "",
    ctx.goalsStrategyStatus
      ? `Overall: ${formatGoalProgressStatus(ctx.goalsStrategyStatus)}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ")
  return {
    reportTitle: "Social Media Goals Overview",
    description: "",
    summaryMetrics: [
      { label: "Active goals", value: formatNumber(summary.total || goals.length) },
      { label: "On track", value: formatNumber(onTrackCount) },
      { label: "At risk", value: formatNumber(atRiskCount) },
      ...(ctx.goalsTimeRemainingText
        ? [{ label: "Time left in period", value: ctx.goalsTimeRemainingText }]
        : []),
    ],
    tables: [
      buildTable(
        "",
        periodHint || "Current goal period",
        ["Goal", "Platform", "Current", "Target", "Progress", "Status", "Period"],
        goals.map((g) => [
          g.name,
          g.platform || "—",
          formatGoalCurrentForExport(g),
          formatGoalTargetForExport(g),
          g.goalProgressLabel || "—",
          formatGoalProgressStatus(g.status),
          formatGoalPeriodForExport(g),
        ]),
        ["text", "platform", "text", "text", "text", "goalStatus", "text"],
        {
          className: "report-table-wrap--goals",
          columnWeights: [2.4, 1.3, 0.9, 1.0, 0.9, 1.0, 1.8],
        },
      ),
    ],
    notes: [],
    insight: buildGoalsOverviewInsight(ctx),
  }
}

function inferAlertBarSeriesFromChartData(chartData, categories = []) {
  const sample = chartData.find((row) => row && typeof row === "object")
  if (!sample) return []
  const keys = Object.keys(sample).filter((key) => key !== "date" && key !== "label")
  if (!keys.length) return []
  const labelByKey = new Map(
    (categories || []).map((cat) => {
      const slug = String(cat?.name || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "")
      return [slug, cat.name]
    }),
  )
  return keys.map((key, index) => {
    const label = labelByKey.get(key) || key.replace(/_/g, " ")
    return {
      key,
      label,
      colorVar: categories[index]?.colorVar || colorVarForAlertTypeLabel(label, index),
    }
  })
}

/** Daily alert rows for bar chart + PDF tables (wide or sparse API shapes). */
export function normalizeAlertsDailyRows(ctx = {}) {
  const chartData = Array.isArray(ctx.alertsChartData) ? ctx.alertsChartData : []
  let series = ctx.alertBarSeries?.length
    ? ctx.alertBarSeries
    : (ctx.alertCategorySeries || []).map((s) => ({
        key: s.key,
        label: s.label,
        colorVar: s.colorVar,
      }))
  if (!series.length && chartData.length) {
    series = inferAlertBarSeriesFromChartData(chartData, ctx.alertCategories)
  }
  if (chartData.length && series.length) {
    return { rows: chartData, series }
  }

  const sparse = ctx.alertsSeries || []
  if (!sparse.length || !series.length) {
    return { rows: chartData, series }
  }

  const byDate = new Map()
  for (const row of sparse) {
    const date = row.date ?? row.label
    if (!date) continue
    if (!byDate.has(date)) {
      const point = { date }
      series.forEach((s) => {
        point[s.key] = 0
      })
      byDate.set(date, point)
    }
    const catKey = row.category ?? row.key
    const val = Number(row.value ?? row.alerts) || 0
    if (catKey) {
      byDate.get(date)[catKey] = (byDate.get(date)[catKey] || 0) + val
    }
  }

  const rows = [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)))
  return { rows, series }
}

export function resolveAlertCategoriesForExport(ctx, series, rows) {
  const cats = ctx.alertCategories || []
  if (cats.length) return cats
  return series
    .map((s) => ({
      name: s.label,
      count: rows.reduce((sum, row) => sum + (Number(row[s.key]) || 0), 0),
    }))
    .filter((c) => c.count > 0)
}

function supplementLineSeriesTable(title, subtitle, series, rows, maxPoints = 31) {
  if (!series?.length || !rows?.length) return null
  const slice = sliceSeriesForReport(rows, maxPoints)
  const columns = ["Date", ...series.map((s) => s.label)]
  const columnTypes = ["date", ...series.map(() => "number")]
  const tableRows = slice.map((row) => [
    row.date ?? row.label,
    ...series.map((s) => row[s.key] ?? row[s.label] ?? 0),
  ])
  return buildTable(title, subtitle, columns, tableRows, columnTypes, {
    className: "report-table-wrap--daily-series",
  })
}

/** Compact tables for PDF (tooltip / hover data not visible in screenshots). */
export function collectChartReportSupplement(chartId, ctx) {
  const tables = []

  switch (chartId) {
    case "campaignPerformance": {
      const events = ctx.campaignEventRows || []
      const series = ctx.campaignBarSeries || []
      if (events.length && series.length) {
        tables.push(
          buildTable(
            "",
            "",
            ["Event", ...series.map((s) => s.label)],
            events.map((e) => [e.eventName ?? e.name, ...series.map((s) => e[s.key] ?? 0)]),
            ["text", ...series.map(() => "number")],
            SIDE_BY_SIDE_CHART_TABLE_OPTIONS,
          ),
        )
      }
      break
    }
    case "messages": {
      const line = supplementLineSeriesTable(
        "Daily messages by platform",
        "Same values as the line-chart tooltip: date plus each platform count.",
        ctx.monitoringLineSeries,
        ctx.monitoringSeries,
        (ctx.monitoringSeries || []).length || 31,
      )
      if (line) tables.push(line)
      break
    }
    case "messageSentiment": {
      const line = supplementLineSeriesTable(
        "Daily sentiment counts",
        "Same values as the line-chart tooltip: date plus each sentiment count.",
        ctx.sentimentLineSeries,
        ctx.sentimentSeries,
        (ctx.sentimentSeries || []).length || 31,
      )
      if (line) tables.push(line)
      break
    }
    case "alerts": {
      const { rows: alertRows, series: alertSeries } = normalizeAlertsDailyRows(ctx)
      const line = supplementLineSeriesTable(
        "Daily alerts by category",
        "Same values as the chart tooltip: date plus each category count.",
        alertSeries,
        alertRows,
        alertRows.length || 31,
      )
      if (line) tables.push(line)
      break
    }
    default:
      break
  }

  return { tables: tables.filter(Boolean) }
}
